// ---------------------------------------------------------------
// PortalLayout.js — Estructura del portal de gestión: sidebar de
// navegación (izquierda), topbar con buscador y avatar, y el
// contenido de la sección activa. En móvil el sidebar se convierte
// en un panel deslizante con overlay.
// ---------------------------------------------------------------
import { cerrarSesion } from "../../services/authService";
import { useEffect, useRef, useState } from "react";
import { avatarStyle } from "../../utils/avatarColor";
import NotificationBell from "./NotificationBell";
import GlobalSearch from "./GlobalSearch";
import {
  IconDashboard,
  IconCalendar,
  IconSchedule,
  IconTimer,
  IconTasks,
  IconGuide,
  IconProfile,
  IconUsers,
  IconImport,
  IconConfig,
  IconSearch,
  IconLogout,
} from "./Icons";
import Icon from "../ui/Icon";
import { useSectionContextValue } from "../../hooks/useSectionContext";
import Footer from "../Footer";
import { useConexion } from "../../hooks/useConexion";

// Cada sección con su color: ayuda a orientarse de un vistazo.
// `group` agrupa el menú por bloques con una etiqueta discreta.
const NAV = [
  { id: "dashboard", label: "Dashboard", icon: <IconDashboard />, color: "#0071e3", group: "Principal" },
  { id: "calendar", label: "Calendario", icon: <IconCalendar />, color: "#ec4899", group: "Principal" },
  { id: "schedule", label: "Horario", icon: <IconSchedule />, color: "#8b5cf6", group: "Principal" },
  { id: "timeclock", label: "Fichaje", icon: <IconTimer />, color: "#0d9488", group: "Principal" },
  { id: "tasks", label: "Tareas", icon: <IconTasks />, color: "#30d158", group: "Principal" },
  { id: "guide", label: "Guía rápida", icon: <IconGuide />, color: "#f59e0b", group: "Principal" },
  { id: "profile", label: "Mi perfil", icon: <IconProfile />, color: "#06b6d4", group: "Principal" },
  { id: "logs", label: "Registro de accesos", icon: <Icon name="shield" size={18} />, color: "#64748b", admin: true, group: "Administración" },
  { id: "notes", label: "Bloc de notas", icon: <Icon name="clipboard" size={18} />, color: "#f43f5e", admin: true, group: "Administración" },
  { id: "users", label: "Usuarios", icon: <IconUsers />, color: "#6366f1", admin: true, group: "Administración" },
  { id: "import", label: "Importar Excel", icon: <IconImport />, color: "#14b8a6", admin: true, group: "Administración" },
  { id: "hoursconfig", label: "Config. horas", icon: <IconConfig />, color: "#d946ef", admin: true, group: "Administración" },
];

// Pestañas de la barra inferior en móvil (al alcance del pulgar)
const TABS = ["dashboard", "schedule", "timeclock", "tasks"];

const TITLES = {
  dashboard: "Dashboard",
  calendar: "Calendario",
  schedule: "Horario",
  timeclock: "Fichaje",
  tasks: "Tareas de turno",
  guide: "Guía rápida",
  profile: "Mi perfil",
  logs: "Registro de accesos",
  notes: "Bloc de notas",
  users: "Gestión de usuarios",
  import: "Importar horarios",
  hoursconfig: "Configuración de horas",
};

const SEARCHABLE = new Set(["users"]);

export default function PortalLayout({
  user,
  profile,
  isAdmin,
  section,
  onSection,
  theme,
  toggleTheme,
  searchTerm,
  onSearch,
  children,
}) {
  const [open, setOpen] = useState(false);
  const enLinea = useConexion();
  const [userMenu, setUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const sectionContext = useSectionContextValue();
  const contentRef = useRef(null);
  const displayName =
    profile?.username || profile?.displayName || user.email?.split("@")[0];
  // Cada sección tiene su color en el menú: la barra superior lo
  // recoge para que se sepa dónde estás sin leer el título
  const sectionColor = NAV.find((n) => n.id === section)?.color;

  // Cerrar el menú del perfil al pulsar fuera o con Escape. Se hace
  // con un escuchador y no con una capa fija: la barra superior lleva
  // backdrop-filter y eso encierra a los `position: fixed` dentro de
  // ella, así que una capa a pantalla completa no funcionaría.
  useEffect(() => {
    if (!userMenu) return undefined;
    const fuera = (e) => {
      if (!userMenuRef.current?.contains(e.target)) setUserMenu(false);
    };
    const tecla = (e) => e.key === "Escape" && setUserMenu(false);
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [userMenu]);

  // Cerrar el menú lateral al tocar fuera de él, o con Escape.
  //
  // La capa oscura ya tenía su `onClick`, pero depender sólo de ella
  // obliga a que su z-index gane a todo lo que haya debajo, y en esta
  // pantalla conviven la barra superior de cristal, la barra inferior
  // de pestañas y el contenido. Con un escuchador en el documento da
  // igual quién esté por encima: si el toque no ha caído dentro del
  // menú, se cierra. Es el mismo patrón que el menú de la cuenta.
  useEffect(() => {
    if (!open) return undefined;
    const fuera = (e) => {
      // Dentro del propio menú: no se toca
      if (e.target.closest?.(".portal-sidebar")) return;
      // Los botones que ABREN el menú: si no, se cerraría en el mismo
      // gesto que lo abre y no llegaría a verse
      if (e.target.closest?.(".hamburger")) return;
      if (e.target.closest?.(".tabbar-mas")) return;
      setOpen(false);
    };
    const tecla = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("pointerdown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [open]);

  // Al cambiar de sección, volver arriba del todo (en móvil se
  // quedaba a la altura del scroll de la sección anterior)
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, left: 0 });
    // En ordenador quien hace scroll es la zona principal (para que la
    // barra superior de cristal tenga contenido pasando por debajo)
    contentRef.current?.parentElement?.scrollTo({ top: 0, left: 0 });
    window.scrollTo(0, 0);
  }, [section]);

  return (
    <div className="portal">
      {/* Sin conexión: se avisa en toda la web. Consultar sigue
          funcionando (Firestore tiene copia local), pero fichar NO: hace
          falta comprobar la red de la farmacia y la hora con el servidor,
          que rechaza un marcaje que llegue tarde. */}
      {!enLinea && (
        <div className="sin-conexion" role="status">
          <Icon name="wifi" size={15} />
          <span>
            Sin conexión. Puedes seguir consultando la web, pero para fichar
            hace falta internet: se comprueban la red de la farmacia y la
            hora con el servidor.
          </span>
        </div>
      )}
      <div
        className={`portal-overlay ${open ? "visible" : ""}`}
        onClick={() => setOpen(false)}
      />

      {/* ---------- Sidebar ---------- */}
      <aside className={`portal-sidebar ${open ? "open" : ""}`}>
        <div className="portal-brand">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Farmacia Buendía"
            className="brand-logo"
          />
          <div className="portal-brand-text">
            <span className="portal-brand-name">Farmacia Buendía</span>
            <span className="portal-brand-sub">Portal de gestión [v1.0.15-d]</span>
          </div>
        </div>

        <nav className="portal-nav">
          {NAV.filter((n) => !n.admin || isAdmin).map((n, i, list) => (
            <div key={n.id} className="nav-entry">
              {/* Etiqueta del bloque, solo al empezar cada grupo */}
              {(i === 0 || list[i - 1].group !== n.group) && (
                <span className="nav-group-label">{n.group}</span>
              )}
              <button
                className={`nav-item ${section === n.id ? "active" : ""}`}
                style={{ "--nav-color": n.color }}
                onClick={() => {
                  onSection(n.id);
                  setOpen(false);
                }}
              >
                {n.icon}
                {n.label}
              </button>
            </div>
          ))}
        </nav>

        {/* Perfil y salida al pie del lateral. El claro/oscuro ya no
            está aquí: vive en la barra superior, donde no hay que
            bajar a buscarlo. */}
        <div className="portal-bottom">
          <div className="portal-profile">
            <div
              className={`avatar small ${isAdmin ? "admin-avatar" : ""}`}
              style={avatarStyle(displayName)}
            >
              {(displayName || "?").charAt(0).toUpperCase()}
            </div>
            <div className="profile-info">
              <span className="profile-name">{displayName}</span>
              <span className="profile-email">
                {isAdmin ? "Administrador" : user.email}
              </span>
            </div>
          </div>

          <button className="signout-btn" onClick={cerrarSesion}>
            <IconLogout />
            Cerrar sesión
          </button>
        </div>

      </aside>

      {/* ---------- Zona principal ---------- */}
      <div className="portal-main">
        <header
          className="portal-topbar"
          style={sectionColor ? { "--section-color": sectionColor } : undefined}
        >
          <button
            className="hamburger"
            onClick={() => setOpen(true)}
            aria-label="Abrir menú"
          >
            <span /><span /><span />
          </button>

          <div className="portal-title-wrap">
            <h1 className="portal-title">{TITLES[section] || ""}</h1>
            {sectionContext && (
              <span className="portal-context">{sectionContext}</span>
            )}
          </div>

          {/* En Usuarios, el buscador filtra la lista; en el resto
              de secciones busca en toda la web */}
          {SEARCHABLE.has(section) ? (
            <div className="topbar-search">
              <IconSearch />
              <input
                type="text"
                placeholder="Buscar usuarios…"
                value={searchTerm}
                onChange={(e) => onSearch(e.target.value)}
              />
            </div>
          ) : (
            <GlobalSearch isAdmin={isAdmin} uid={user.uid} onNavigate={onSection} />
          )}
          {/* Empuja SIEMPRE la campana y el avatar a la derecha */}
          <div className="topbar-spacer" />

          <NotificationBell
            user={user}
            profile={profile}
            isAdmin={isAdmin}
            onNavigate={onSection}
          />

          {/* Claro/oscuro aquí arriba: antes estaba al final del menú
              lateral y con tantas secciones había que bajar a buscarlo */}
          <button
            className="topbar-theme"
            onClick={toggleTheme}
            title={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
            aria-label={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} size={18} />
          </button>

          <div className="topbar-user-wrap" ref={userMenuRef}>
            <button
              className="topbar-user"
              onClick={() => setUserMenu((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={userMenu}
              title="Tu cuenta"
            >
              <div className="profile-info">
                <span className="profile-name">{displayName}</span>
              </div>
              <div
                className={`avatar ${isAdmin ? "admin-avatar" : ""}`}
                style={avatarStyle(displayName)}
              >
                {(displayName || "?").charAt(0).toUpperCase()}
              </div>
            </button>

            {userMenu && (
              <div className="user-menu" role="menu">
                <div className="user-menu-head">
                  <span className="user-menu-name">{displayName}</span>
                  <span className="user-menu-sub">
                    {isAdmin ? "Administrador" : user.email}
                  </span>
                </div>
                <button
                  className="user-menu-item"
                  role="menuitem"
                  onClick={() => {
                    setUserMenu(false);
                    onSection("profile");
                  }}
                >
                  <Icon name="person" size={15} /> Mi perfil
                </button>
                <button
                  className="user-menu-item danger"
                  role="menuitem"
                  onClick={cerrarSesion}
                >
                  <Icon name="lock" size={15} /> Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Barra de pestañas inferior (solo móvil): lo más usado al
            alcance del pulgar; "Más" abre el menú lateral completo */}
        <nav className="tabbar" aria-label="Navegación principal">
          {TABS.map((id) => {
            const n = NAV.find((x) => x.id === id);
            return (
              <button
                key={id}
                className={`tabbar-item ${section === id ? "active" : ""}`}
                style={{ "--nav-color": n.color }}
                onClick={() => onSection(id)}
              >
                {n.icon}
                <span>{n.label}</span>
              </button>
            );
          })}
          <button
            className={`tabbar-item tabbar-mas ${!TABS.includes(section) ? "active" : ""}`}
            onClick={() => setOpen(true)}
            aria-label="Más secciones"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
            <span>Más</span>
          </button>
        </nav>

        <div className="portal-content" ref={contentRef}>
          {/* key = sección: al cambiar, el contenido entra con un
              fundido (sólo opacidad/desplazamiento: no penaliza) */}
          <div key={section} className="section-fade">
            {children}
          </div>
          <Footer />
        </div>
      </div>
    </div>
  );
}
